import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import { ChevronRight, Trash2, Loader2, X, Truck, Clock, Lock } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const CAT_BADGE = {
  ecu: { label: "ECU", color: "#00E5FF" },
  gearbox: { label: "Boîte/TCU", color: "#FFB000" },
  airbag: { label: "Airbag/SRS", color: "#00E5FF" },
  battery: { label: "Batterie/Hybride", color: "#00E5A0" },
};

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "received", label: "Reçu" },
  { id: "in_progress", label: "En cours" },
  { id: "completed", label: "Terminé" },
  { id: "rejected", label: "Refusé" },
  { id: "encrypted", label: "Cryptés", accent: "amber" },
];

const isEncrypted = (o) => !!(o.is_slave_order || o.encrypted);

export const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? orders
    : filter === "encrypted" ? orders.filter(isEncrypted)
    : orders.filter((o) => o.status === filter);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every((o) => selected.has(o.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filtered.forEach((o) => n.delete(o.id));
    else filtered.forEach((o) => n.add(o.id));
    return n;
  });

  const deleteOne = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette commande ? (fichiers + messages)")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      setOrders((o) => o.filter((x) => x.id !== id));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.success("Commande supprimée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} commande(s) ? Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => api.delete(`/admin/orders/${id}`).catch(() => null)));
      setOrders((o) => o.filter((x) => !selected.has(x.id)));
      setSelected(new Set());
      toast.success(`${ids.length} commande(s) supprimée(s)`);
    } catch (e) { toast.error("Erreur lors de la suppression"); }
    finally { setDeleting(false); }
  };

  return (
    <AppShell mode="admin">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Traitement</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">File de traitement</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => {
          const count = f.id === "all" ? orders.length
            : f.id === "encrypted" ? orders.filter(isEncrypted).length
            : orders.filter((o) => o.status === f.id).length;
          const active = filter === f.id;
          const amber = f.accent === "amber";
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors flex items-center gap-1.5 ${
                active
                  ? amber ? "border-amber-400 text-amber-400 bg-amber-400/10" : "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10"
                  : amber ? "border-amber-400/30 text-amber-400/80 hover:border-amber-400/60" : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {amber && <Lock className="w-3 h-3" />}
              {f.label} <span className={active && amber ? "text-amber-400/60" : "text-zinc-600"}>({count})</span>
            </button>
          );
        })}
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest rounded-sm border border-white/15 text-zinc-400 hover:border-white/30 flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> {selected.size}
            </button>
            <button data-testid="delete-selected-btn" onClick={deleteSelected} disabled={deleting}
              className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest rounded-sm bg-signal-error text-white hover:bg-red-500 transition-colors flex items-center gap-1.5 disabled:opacity-50">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Supprimer la sélection
            </button>
          </div>
        )}
      </div>

      <div className="glass rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 items-center">
          <div className="col-span-1">
            <input type="checkbox" data-testid="select-all" checked={allSelected} onChange={toggleAll} className="accent-aurora-cyan w-4 h-4 cursor-pointer" />
          </div>
          <div className="col-span-3">Client</div>
          <div className="col-span-3">Véhicule</div>
          <div className="col-span-2">Fichier</div>
          <div className="col-span-2">Solutions</div>
          <div className="col-span-1 text-right">Statut</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">Chargement...</div>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm py-16 text-center">Aucun fichier dans cette catégorie.</p>
        ) : (
          filtered.map((o) => (
            <div
              key={o.id}
              data-testid={`admin-order-${o.id}`}
              onClick={() => navigate(`/admin/orders/${o.id}`)}
              className={`grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors items-center cursor-pointer ${selected.has(o.id) ? "bg-aurora-cyan/5" : ""}`}
            >
              <div className="md:col-span-1 hidden md:block" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" data-testid={`select-order-${o.id}`} checked={selected.has(o.id)} onChange={() => toggle(o.id)} className="accent-aurora-cyan w-4 h-4 cursor-pointer" />
              </div>
              <div className="md:col-span-3 min-w-0">
                <div className="text-sm truncate">{o.user_name}</div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{o.user_email}</div>
                <div className="font-mono text-[10px] text-zinc-600 truncate flex items-center gap-1 mt-0.5"><Clock className="w-2.5 h-2.5" />{fmtDateTime(o.created_at, "fr")}</div>
              </div>
              <div className="md:col-span-3 font-display uppercase tracking-tight hidden md:block">
                {o.vehicle_brand} {o.vehicle_model}
                {o.vehicle_class === "truck_agri" && <span data-testid={`order-truck-${o.id}`} className="ml-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FFB020]/10 text-[#FFB020] border border-[#FFB020]/40 align-middle"><Truck className="w-2.5 h-2.5" /> 24T·AGRI</span>}
                {(o.is_slave_order || o.encrypted) && <span data-testid={`order-slave-${o.id}`} title={`Fichier crypté · Slave${o.slave_tool ? " · " + o.slave_tool : ""} — traitement manuel`} className="ml-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-400/10 text-amber-400 border border-amber-400/40 align-middle"><Lock className="w-2.5 h-2.5" /> CRYPTÉ{o.slave_tool ? `·${o.slave_tool}` : ""}</span>}
              </div>
              <div className="md:col-span-2 font-mono text-[10px] text-zinc-400 truncate hidden md:block">{o.original_filename}</div>
              <div className="md:col-span-2 hidden md:block min-w-0">
                {(() => { const c = CAT_BADGE[o.category || "ecu"] || CAT_BADGE.ecu; return (
                  <span
                    data-testid={`order-category-${o.id}`}
                    className="inline-block font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm mb-1"
                    style={{ color: c.color, background: `${c.color}1a`, border: `1px solid ${c.color}40` }}
                  >{c.label}</span>
                ); })()}
                <div className="font-mono text-[10px] text-zinc-500 truncate">{o.services.join(", ")}</div>
              </div>
              <div className="md:col-span-1 flex justify-end items-center gap-2">
                <StatusPill status={o.status} />
                <button
                  data-testid={`delete-order-${o.id}`}
                  onClick={(e) => deleteOne(o.id, e)}
                  title="Supprimer"
                  className="w-7 h-7 grid place-items-center rounded-sm border border-white/10 text-zinc-500 hover:border-signal-error hover:text-signal-error transition-colors shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <ChevronRight className="w-4 h-4 text-zinc-600 hidden md:block" />
              </div>
            </div>
          ))
        )}
      </div>
    </AppShell>
  );
};
